import { RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { getRowOffsets, getVirtualRange } from './virtualTableLayout'

export type VirtualRowProps = {
	rowId: string
	rowIndex: number
	measureRow: (id: string, element: HTMLTableRowElement | null) => void
}

/** Window native table rows using spacer rows, without transforms that break sticky cells. */
export function useVirtualTableRows(
	rows: { id: string; kind: string }[],
	containerRef: RefObject<HTMLDivElement>,
	tableRef: RefObject<HTMLTableElement>,
	freezeEnabled: boolean,
) {
	const heights = useRef(new Map<string, number>())
	const elements = useRef(new Map<string, HTMLTableRowElement>())
	const observer = useRef<ResizeObserver>()
	const [revision, setRevision] = useState(0)
	const [viewport, setViewport] = useState({ top: 0, height: 800 })
	const offsets = useMemo(() => getRowOffsets(rows, heights.current), [rows, revision])
	const indices = useMemo(() => new Map(rows.map((row, index) => [row.id, index])), [rows])
	const layout = useRef({ rows, offsets, indices })
	layout.current = { rows, offsets, indices }
	const range = getVirtualRange(offsets, viewport.top, viewport.height)

	const measureRow = useCallback((id: string, element: HTMLTableRowElement | null) => {
		const previous = elements.current.get(id)
		if (previous) observer.current?.unobserve(previous)
		if (element) {
			elements.current.set(id, element)
			observer.current?.observe(element)
		} else elements.current.delete(id)
	}, [])

	useLayoutEffect(() => {
		const container = containerRef.current
		const table = tableRef.current
		if (!container || !table) return
		let frame = 0
		let lastWidth = container.clientWidth
		const update = () => {
			frame = 0
			const headerHeight = table.tHead?.getBoundingClientRect().height ?? 0
			const top = container.scrollTop
			const height = Math.max(1, container.clientHeight - headerHeight)
			setViewport((previous) =>
				previous.top === top && previous.height === height ? previous : { top, height },
			)
		}
		const scheduleUpdate = () => {
			if (!frame) frame = requestAnimationFrame(update)
		}
		container.addEventListener('scroll', scheduleUpdate, { passive: true })
		const resize = new ResizeObserver(() => {
			if (container.clientWidth !== lastWidth) {
				lastWidth = container.clientWidth
				heights.current.clear()
				// Let the native table recompute responsive widths before remembering them again.
				Array.from(table.tHead?.rows[0]?.cells ?? []).forEach((cell) =>
					cell.style.removeProperty('min-width'),
				)
				elements.current.forEach((element, id) => {
					const height = element.getBoundingClientRect().height
					if (height > 0) heights.current.set(id, height)
				})
				setRevision((value) => value + 1)
			}
			scheduleUpdate()
		})
		resize.observe(container)
		if (table.tHead) resize.observe(table.tHead)
		observer.current = new ResizeObserver((entries) => {
			let changed = false
			let adjustment = 0
			const { offsets: currentOffsets, indices: currentIndices } = layout.current
			const ids = new Map(Array.from(elements.current, ([id, element]) => [element, id]))
			for (const entry of entries) {
				const id = ids.get(entry.target as HTMLTableRowElement)
				if (!id) continue
				const height = entry.target.getBoundingClientRect().height
				const index = currentIndices.get(id)
				if (height <= 0 || index === undefined || heights.current.get(id) === height) continue
				// Keep the visible content anchored when an overscan row above it changes height.
				if (currentOffsets[index + 1] <= container.scrollTop) {
					adjustment += height - (currentOffsets[index + 1] - currentOffsets[index])
				}
				heights.current.set(id, height)
				if (height !== currentOffsets[index + 1] - currentOffsets[index]) changed = true
			}
			if (changed) {
				container.scrollTop += adjustment
				setRevision((value) => value + 1)
				scheduleUpdate()
			}
		})
		elements.current.forEach((element) => observer.current?.observe(element))
		update()
		return () => {
			cancelAnimationFrame(frame)
			container.removeEventListener('scroll', scheduleUpdate)
			resize.disconnect()
			observer.current?.disconnect()
		}
	}, [containerRef, tableRef])

	useLayoutEffect(() => {
		heights.current.clear()
		Array.from(tableRef.current?.tHead?.rows[0]?.cells ?? []).forEach((cell) =>
			cell.style.removeProperty('min-width'),
		)
		setRevision((value) => value + 1)
		// Freezing can wrap names; remeasure even when a row's outer size did not change.
		elements.current.forEach((element, id) => {
			const height = element.getBoundingClientRect().height
			if (height > 0) heights.current.set(id, height)
		})
	}, [freezeEnabled, tableRef])

	useLayoutEffect(() => {
		const table = tableRef.current
		const container = containerRef.current
		if (!table || !container) return
		// Preserve native content-sized columns as rows leave the DOM, avoiding horizontal jitter.
		Array.from(table.tHead?.rows[0]?.cells ?? []).forEach((cell, index) => {
			if (freezeEnabled && index < 6) return // Existing freeze CSS owns these responsive widths.
			const width = cell.getBoundingClientRect().width
			if (width > 0) cell.style.minWidth = `${width}px`
		})
		const maxTop = Math.max(0, offsets[offsets.length - 1] - viewport.height)
		if (container.scrollTop > maxTop) {
			container.scrollTop = maxTop
			setViewport((previous) => ({ ...previous, top: maxTop }))
		}
	}, [
		rows,
		range.start,
		range.end,
		offsets,
		viewport.height,
		freezeEnabled,
		tableRef,
		containerRef,
	])

	const scrollToRow = useCallback(
		(id: string) => {
			const { rows: currentRows, offsets: currentOffsets } = layout.current
			const index = currentRows.findIndex((row) => row.id === id)
			const container = containerRef.current
			if (index < 0 || !container) return
			container.scrollTop = Math.max(0, currentOffsets[index] - container.clientHeight / 2)
			setViewport((previous) => ({ ...previous, top: container.scrollTop }))
		},
		[containerRef],
	)

	return { ...range, measureRow, scrollToRow }
}
